/**
 * CheckboxesField.tsx
 *
 * Unified checkboxes/radio field component
 * Supports both single selection (radio buttons) and multiple selection (checkboxes)
 * based on the allowMultiple configuration
 */

import React from 'react';
import { BaseFieldProps } from './shared/types';
import { FieldWrapper } from './shared';

export const CheckboxesField: React.FC<BaseFieldProps> = React.memo(({
  fieldConfig,
  mode,
  value,
  onChange,
  errors = [],
  disabled = false,
  hasError = false,
}) => {
  const isPreviewMode = mode === 'preview';
  const isDisabled = disabled || isPreviewMode;

  // Determine if we're in multi-select mode (default to false for single selection / radio buttons)
  const allowMultiple = fieldConfig.allowMultiple === true;

  // Parse the current value
  // For multiple selection: value should be an array or comma-separated string
  // For single selection: value should be a single string
  const selectedValues: string[] = React.useMemo(() => {
    if (!value) return [];

    let parsed: string[];
    if (Array.isArray(value)) {
      parsed = value;
    } else if (typeof value === 'string') {
      if (allowMultiple && value.includes(',')) {
        parsed = value.split(',').map(v => v.trim());
      } else {
        parsed = value ? [value] : [];
      }
    } else {
      return [];
    }

    // In radio (single-select) mode, only the first value is valid.
    // Guards against a stale array left over from a previous multi-select state.
    return allowMultiple ? parsed : parsed.slice(0, 1);
  }, [value, allowMultiple]);

  const handleCheckboxChange = (option: string, checked: boolean) => {
    if (isPreviewMode) return;

    if (allowMultiple) {
      let newValues: string[];
      if (checked) {
        newValues = [...selectedValues, option];
      } else {
        newValues = selectedValues.filter(v => v !== option);
      }
      onChange(fieldConfig.id, newValues);
    } else {
      onChange(fieldConfig.id, checked ? option : '');
    }
  };

  const handleRadioChange = (option: string) => {
    if (isPreviewMode) return;
    onChange(fieldConfig.id, option);
  };

  const isChecked = (option: string): boolean => {
    if (!allowMultiple) {
      // Radio mode: only the single normalized value can be checked.
      return selectedValues[0] === option;
    }
    return selectedValues.includes(option);
  };

  const options = fieldConfig.options || [];

  return (
    <FieldWrapper
      field={fieldConfig}
      errors={errors}
      hasError={hasError}
      isPreview={isPreviewMode}
    >
      <div className="space-y-2">
        {options.map((option: any, idx: number) => {
          const optionValue = typeof option === 'string' ? option : option.value;
          const optionLabel = typeof option === 'string' ? option : option.label;
          const inputId = `${fieldConfig.id}-option-${idx}`;
          const checked = isChecked(optionValue);

          return (
            <div key={idx} className="flex items-center gap-2">
              {allowMultiple ? (
                // Checkbox for multiple selection
                <input
                  type="checkbox"
                  id={inputId}
                  name={fieldConfig.id}
                  value={optionValue}
                  checked={checked}
                  onChange={(e) => handleCheckboxChange(optionValue, e.target.checked)}
                  disabled={isDisabled}
                  className={`h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 ${
                    isDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
                  } ${hasError ? 'border-red-500' : ''}`}
                />
              ) : (
                // Radio button for single selection
                <input
                  type="radio"
                  id={inputId}
                  name={fieldConfig.id}
                  value={optionValue}
                  checked={checked}
                  onChange={() => handleRadioChange(optionValue)}
                  disabled={isDisabled}
                  className={`h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500 ${
                    isDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
                  } ${hasError ? 'border-red-500' : ''}`}
                />
              )}
              <label
                htmlFor={inputId}
                className={`text-sm ${
                  isDisabled ? 'cursor-not-allowed text-gray-400' : 'cursor-pointer text-gray-700'
                }`}
              >
                {optionLabel}
              </label>
            </div>
          );
        })}
        {options.length === 0 && (
          <p className="text-sm text-gray-400">No options configured</p>
        )}
      </div>
    </FieldWrapper>
  );
});

CheckboxesField.displayName = 'CheckboxesField';
